import { useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const UNDO_WINDOW_MS = 8000;

/**
 * Defer a delete operation by UNDO_WINDOW_MS so the user can undo via a sonner toast action.
 *
 * Usage:
 *   const { scheduleDelete } = useUndoDelete();
 *   scheduleDelete({
 *     id: item.id,
 *     label: 'Account',
 *     executor: async () => axios.delete(`${API}/accounts/${item.id}`),
 *     onUndo: () => setAccounts(prev => [item, ...prev]),
 *   });
 *
 * Caller is responsible for the optimistic UI removal — this hook only handles the
 * timer, the toast, and flushing pending deletes on unmount.
 */
export default function useUndoDelete() {
  const pendingRef = useRef(new Map());

  // On unmount, flush any pending deletes (fire executor immediately)
  useEffect(() => {
    const map = pendingRef.current;
    return () => {
      for (const { timer, executor } of map.values()) {
        clearTimeout(timer);
        try {
          executor();
        } catch (e) {
          // Unmount-flush is best-effort — caller already saw the optimistic UI update.
          console.error('[useUndoDelete] flush executor failed:', e);
        }
      }
      map.clear();
    };
  }, []);

  const scheduleDelete = useCallback(({ id, label = 'Item', executor, onUndo, onAfterDelete }) => {
    // Cancel any prior scheduled delete for the same id
    const existing = pendingRef.current.get(id);
    if (existing) {
      clearTimeout(existing.timer);
    }

    const finalize = async () => {
      pendingRef.current.delete(id);
      try {
        await executor();
        if (onAfterDelete) onAfterDelete();
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Delete failed — refreshing`);
        if (onAfterDelete) onAfterDelete();
      }
    };

    const timer = setTimeout(finalize, UNDO_WINDOW_MS);
    pendingRef.current.set(id, { timer, executor: finalize });

    toast.success(`${label} deleted`, {
      duration: UNDO_WINDOW_MS,
      action: {
        label: 'Undo',
        onClick: () => {
          clearTimeout(timer);
          pendingRef.current.delete(id);
          if (onUndo) onUndo();
          toast(`${label} restored`);
        },
      },
    });
  }, []);

  return { scheduleDelete };
}
